import logger from "debug";
import { requestBase, fetchYou, ApiResponse } from "../helpers";
import * as _ from "lodash";
import { Rate } from "../components/pricing/types/RateTypes";
import { Hotel, RatePlan, RoomType } from "./types/PricingApiTypes";

const apiBase = "api/pricing";
const debug = logger("pricing:pricing-api");

const getHeaders = (req: BearerRequest) => {
  let { bearer } = req;

  let headers: HeadersInit = requestBase.headers as Headers;
  headers.set("Authorization", `Bearer ${bearer}`);

  return headers;
};

interface BearerRequest {
  bearer: string;
}

interface GetRoomTypeRequest {
  hotelId: number | string | null;
}

interface GetRatePlanRequest extends GetRoomTypeRequest {
  roomTypeId: number | string | null;
}

interface GetRatesRequest extends GetRatePlanRequest {
  ratePlanId: number | string | null;
  startDate: Date | null | undefined;
  endDate: Date | null | undefined;
}

interface UpdateRatesRequest {
  filter: GetRatesRequest;
  rates: Array<Rate>;
}

interface PricingApi {
  initialize: (bearer: string) => Promise<ApiResponse<HotelResponse>>;
  getRoomTypes: (
    bearer: string,
    req: GetRoomTypeRequest
  ) => Promise<ApiResponse<RoomTypeResponse>>;
  getRatePlans: (
    bearer: string,
    req: GetRatePlanRequest
  ) => Promise<ApiResponse<RatePlanResponse>>;
  getRates: (
    bearer: string,
    req: GetRatesRequest
  ) => Promise<ApiResponse<RatesResponse>>;
  updateRates: (
    bearer: string,
    req: UpdateRatesRequest
  ) => Promise<ApiResponse<UpdateRatesResponse>>;
}

interface HotelResponse extends Array<Hotel> {}
interface RoomTypeResponse extends Array<RoomType> {}
interface RatePlanResponse extends Array<RatePlan> {}
interface RatesResponse {
  markup: number;
  rates: Array<Rate>;
}

interface UpdateRatesResponse {
  success: boolean;
  message: string;
}

const pricingApi: PricingApi = {
  /**
   * @typedef {Function} initialize - initializes pricing page with init data
   * @returns {Object} initialization response
   */
  initialize: (bearer) => {
    let request = Object.assign({}, requestBase, {
      method: "GET",
      headers: getHeaders({ bearer }),
    });

    console.log("initRequest", request);

    const url = `${apiBase}`;
    return fetchYou<HotelResponse>(url, request);
  },
  getRoomTypes: (bearer, { hotelId }) => {
    let request = Object.assign({}, requestBase, {
      headers: getHeaders({ bearer }),
      body: JSON.stringify({ hotelId }),
    });

    console.log("getRoomTypesRequest", request);
    const url = `${apiBase}/getroomtypes`;
    return fetchYou<RoomTypeResponse>(url, request);
  },
  getRatePlans: (bearer, { hotelId, roomTypeId }) => {
    let request = Object.assign({}, requestBase, {
      headers: getHeaders({ bearer }),
      body: JSON.stringify({ hotelId, roomTypeId }),
    });

    const url = `${apiBase}/getrateplans`;
    return fetchYou<RatePlanResponse>(url, request);
  },
  getRates: (
    bearer,
    { hotelId, roomTypeId, ratePlanId, startDate, endDate }
  ) => {
    let request = Object.assign({}, requestBase, {
      headers: getHeaders({ bearer }),
      body: JSON.stringify({
        hotelId,
        roomTypeId,
        ratePlanId,
        startDate,
        endDate,
      }),
    });

    const url = `${apiBase}/getrates`;
    return fetchYou<RatesResponse>(url, request);
  },
  updateRates: (bearer, { filter, rates }) => {
    const { hotelId, roomTypeId, ratePlanId, startDate, endDate } = filter;

    let request = Object.assign({}, requestBase, {
      headers: getHeaders({ bearer }),
      body: JSON.stringify({
        filter: { hotelId, roomTypeId, ratePlanId, startDate, endDate },
        rates,
      }),
    });

    const url = `${apiBase}/updaterates`;
    return fetchYou<UpdateRatesResponse>(url, request);
  },
};

const apiInstance = Object.freeze(pricingApi);
export { apiInstance as PricingApi };
